import React from "react";
import { Link } from "react-router-dom";
import { ArrowDown, Camera } from "lucide-react";
import { LOGO_FULL } from "@/lib/data";
import { BokehParticles } from "@/components/effects/BokehParticles";
import { LightStreaks } from "@/components/effects/LightStreaks";
import { DustParticles } from "@/components/effects/DustParticles";
import { InstantPhotoReel } from "@/components/effects/InstantPhotoReel";
import { Reveal } from "@/components/effects/Reveal";

export const Hero = () => {
  return (
    <section
      data-testid="home-hero"
      className="relative min-h-[100svh] w-full overflow-hidden flex items-center justify-center"
      style={{
        background:
          "radial-gradient(80% 60% at 50% 0%, rgba(255,46,147,0.10) 0%, transparent 60%)," +
          "radial-gradient(70% 60% at 50% 100%, rgba(24,211,255,0.08) 0%, transparent 60%)," +
          "linear-gradient(180deg, #000 0%, #050507 50%, #000 100%)",
      }}
    >
      {/* Lens flare radial */}
      <div className="lens-flare -top-40 -left-40" />
      <div className="lens-flare -bottom-40 -right-40" style={{ animationDelay: "-4s" }} />

      {/* Aperture rings */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="aperture-ring w-[80vmin] h-[80vmin] opacity-25" />
        <div className="aperture-ring absolute w-[60vmin] h-[60vmin] opacity-15" style={{ animationDirection: "reverse", animationDuration: "55s" }} />
        <div className="aperture-ring absolute w-[40vmin] h-[40vmin] opacity-10" />
      </div>

      {/* Atmospheric layers */}
      <LightStreaks count={6} />
      <BokehParticles count={16} />
      <DustParticles count={18} />

      {/* Instant photo reel — sits BEHIND the centered content. Desktop only;
          mobile keeps a cleaner composition (bokeh + streaks + dust). */}
      <div className="absolute inset-0 max-w-[1280px] mx-auto px-6 sm:px-10 hidden md:block">
        <InstantPhotoReel />
      </div>

      {/* Top hairline */}
      <div className="absolute top-0 inset-x-0 glow-line-mag" />

      <div className="relative z-10 max-w-[1280px] w-full mx-auto px-6 sm:px-10 pt-32 pb-24 flex flex-col items-center text-center">
        <Reveal>
          <p className="text-[11px] sm:text-xs tracking-luxe text-white/55 uppercase mb-6 inline-flex items-center gap-3">
            <span className="w-8 h-px bg-gradient-to-r from-[#ff2e93] to-transparent" />
            Est. 2025 · Goldfields, South Africa
            <span className="w-8 h-px bg-gradient-to-l from-[#18d3ff] to-transparent" />
          </p>
        </Reveal>

        <Reveal delay={120}>
          {/* Subtle spotlight behind the logo so it feels lit from above */}
          <div className="relative inline-block">
            <span
              aria-hidden
              className="absolute -inset-10 sm:-inset-16 rounded-full pointer-events-none"
              style={{
                background:
                  "radial-gradient(closest-side, rgba(255,46,147,0.18), rgba(24,211,255,0.10) 60%, transparent 75%)",
                filter: "blur(20px)",
              }}
            />
            <img
              src={LOGO_FULL}
              alt="WelkomBiz Photography"
              className="relative w-[220px] sm:w-[280px] md:w-[320px] h-auto mx-auto mb-8 select-none drop-shadow-[0_18px_30px_rgba(0,0,0,0.5)]"
              style={{ background: "transparent", objectFit: "contain" }}
              draggable={false}
            />
          </div>
        </Reveal>

        <Reveal delay={220}>
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[1.02] max-w-5xl">
            <span className="text-chrome">Capturing Life’s</span>
            <br />
            <span className="italic font-light text-magcyan">Finest Moments</span>
            <br />
            <span className="text-chrome">with Premium Elegance</span>
          </h1>
        </Reveal>

        <Reveal delay={360}>
          <p className="mt-8 text-white/65 max-w-2xl text-base sm:text-lg leading-relaxed font-body">
            WelkomBiz Photography delivers top-quality event photography, cinematic visual
            storytelling, and premium media solutions for unforgettable moments.
          </p>
        </Reveal>

        <Reveal delay={500}>
          <div className="mt-12 flex flex-col sm:flex-row items-center gap-4">
            <Link to="/events" data-testid="hero-cta-events" className="btn-primary cta-pulse">
              <Camera className="w-3.5 h-3.5" />
              View Events
            </Link>
            <Link to="/hire" data-testid="hero-cta-hire" className="btn-ghost">
              Hire Us
            </Link>
            <Link to="/contact" data-testid="hero-cta-contact" className="btn-ghost">
              Contact Us
            </Link>
          </div>
        </Reveal>

        <Reveal delay={700}>
          <div className="mt-20 flex flex-col items-center text-white/40">
            <span className="text-[10px] tracking-luxe uppercase mb-2">Scroll</span>
            <ArrowDown className="w-4 h-4 animate-bounce" />
          </div>
        </Reveal>
      </div>

      <div className="absolute bottom-0 inset-x-0 glow-line-cy" />
    </section>
  );
};

export default Hero;
